/* eslint-disable */
import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { PopupService } from '@app/core/services/popup.service';
import { ProfileService } from '@app/layout/common/profile/state/profile.service';
import { ProfileState } from '@app/layout/common/profile/state/profile.state';
import { Profile } from '@app/layout/common/profile/state/profile.types';
import { TranslocoService } from '@ngneat/transloco';
import { rxActions } from '@rx-angular/state/actions';
import * as Sentry from '@sentry/angular';
import { catchError, exhaustMap, filter, of, switchMap, tap } from 'rxjs';

interface IProfileAction {
  get: void;
  getSuccess: {
    profile: Profile;
  };
  getLandingPage: void;
  getLandingPageSuccess: {
    profile: Profile;
  };
  updateProfile: {
    id: string;
    profile: Profile;
  };
  updateProfileSuccess: {
    profile: Profile;
  };
}

@Injectable({
  providedIn: 'root'
})
export class ProfileAction {
  actions = rxActions<IProfileAction>();

  private readonly _profileService = inject(ProfileService);
  private readonly _profileState = inject(ProfileState);
  private readonly _popupService = inject(PopupService);
  private readonly _translocoService: TranslocoService = inject(TranslocoService);

  constructor() {
    this._subscribeToGetProfile$();
    this._subscribeToGetProfileSuccess$();
    this._subscribeToUpdateProfile$();
    this._subscribeToUpdateProfileSuccess$();
    this._subscribeToGetLandingPage$();
    this._subscribeToGetLandingPageSuccess$();
  }

  private _subscribeToGetProfile$(): void {
    this.actions.onGet(
      (payload$) =>
        payload$.pipe(
          switchMap(() =>
            this._profileService.get().pipe<Profile, Profile>(
              catchError((error) => of(error)),
              filter((response) => !(response instanceof HttpErrorResponse))
            )
          )
        ),
      (profile) => {
        Sentry.setUser({
          id: String(profile.id),
          email: profile.email,
          username: profile.name,
          segment: profile.role_name
        });
        this._profileState.set({ profile });
        this.actions.getSuccess({
          profile
        });
      }
    );
  }

  private _subscribeToGetProfileSuccess$(): void {
    this.actions.getSuccess$.subscribe({
      next: ({ profile }) => {
        this._profileState.set({
          profile
        });
      }
    });
  }

  private _subscribeToUpdateProfile$(): void {
    this.actions.onUpdateProfile(
      (payload$) =>
        payload$.pipe(
          tap(() => {
            this._popupService.loadingDialog();
          }),
          exhaustMap((payload) =>
            this._profileService.updateProfile(payload.id, payload.profile).pipe<Profile, Profile>(
              catchError((error) => {
                this._popupService.closeLoadingDialog();
                return of(error);
              }),
              filter((response) => !(response instanceof HttpErrorResponse))
            )
          )
        ),
      (profile) => {
        this._popupService.messageSnackbar({
          type: 'success',
          message: this._translocoService.translate('setting.profile.profile_updated_message')
        });
        this.actions.updateProfileSuccess({ profile });
        this._popupService.closeLoadingDialog();
      }
    );
  }

  private _subscribeToUpdateProfileSuccess$(): void {
    this.actions.updateProfileSuccess$.subscribe({
      next: ({ profile }) => {
        this._profileState.set({
          profile
        });
      }
    });
  }

  private _subscribeToGetLandingPage$(): void {
    this.actions.onGetLandingPage(
      (payload$) =>
        payload$.pipe(
          switchMap(() =>
            this._profileService.getLandingPage().pipe<Profile, Profile>(
              catchError((error) => of(error)),
              filter((response) => !(response instanceof HttpErrorResponse))
            )
          )
        ),
      (profile) => {
        this.actions.getLandingPageSuccess({
          profile
        });
      }
    );
  }

  private _subscribeToGetLandingPageSuccess$(): void {
    this.actions.getLandingPageSuccess$.subscribe({
      next: ({ profile }) => {
        this._profileState.set({
          profile
        });
      }
    });
  }
}
